import { Suspense, useCallback, useEffect, useState } from 'react';
import { useDebounce } from 'primereact/hooks';
import { useChildPageTitle } from '../custom-hooks/useChildPageTitle';
import { Skill } from '../models/Skill';
import { SkillSearchResults } from '../components/SingleUnit/SkillSearchResults';
import SingleUnitSkillDialog from '../components/SingleUnit/SingleUnitSkillDialog';
import { useSideNavActive } from '../components/LayoutWrapper/SideNavLayoutContext';
import { selectSelectedClassIds } from '../redux/reducers/globalClassFilterSlice';
import { Class } from '../models/Class';
import { Toolbar } from 'primereact/toolbar';
import { BreadCrumb } from 'primereact/breadcrumb';
import { ThreeDotLoader } from '../components/Fallback/ThreeDotsLoader';
import SingleUnitContainer from '../components/SingleUnit/SingleUnitContainer';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { selectUser } from '../redux/reducers/userSlice';
import { setTargetGroup } from '../redux/reducers/targetGroupSlice';
import { setUnit } from '../redux/reducers/unitSlice';
import { CustomBranding, mapCustomBranding } from '../models/User';
import { Unit } from '../models/Unit';
import { sortByKey, sortByLabelWithDot } from '../businessLogicService/sortCriteria';
import { useLazyLoadQuery } from 'react-relay';
import { DashboardAllUnitsQuery } from '../graphQLOperations/__generated__/DashboardAllUnitsQuery.graphql';
import { getAllUnitsGraphQL } from '../graphQLOperations/Dashboard';
import { getCurriculumLessons } from '../graphQLOperations/SingleUnit';
import { SingleUnitGetCurriculumLessonsQuery } from '../graphQLOperations/__generated__/SingleUnitGetCurriculumLessonsQuery.graphql';
import removeTargetDuplicates from '../businessLogicService/removeTargetDuplicates';
import { useGetClassesByRole } from '../custom-hooks/useGetClassesByRole';
import { SitesTargetGroupsQuery } from '../graphQLOperations/__generated__/SitesTargetGroupsQuery.graphql';
import { sitesTargetGroupsQuery } from '../graphQLOperations/Sites';
import SingleUnitFilter from '../components/SingleUnit/SingleUnitFilter';
import UnitsReportSettings from '../components/SingleUnit/UnitsReportSettings';
import UnitsOptionalFilters from '../components/SingleUnit/UnitsOptionalFilters';
import { isUserAdmin } from '../businessLogicService/isUserAdmin';

export interface FormState {
  roadmap: DropdownItemModel | null;
  unit: DropdownItemModel | null;
  lesson: string[]; // optional
  standards: string[]; // optional
  class: Class[];
  unitEnabled: boolean;
  lessonEnabled: boolean;
  classEnabled: boolean;
}

export interface AvailablesOptions {
  roadmap: DropdownItemModel[];
  unit: DropdownItemModel[];
  lesson: DropdownItemModel[]; // optional
  class: Class[];
  unitRaw: Unit[];
}

export interface DropdownItemModel {
  value: string;
  label: string;
}
export interface FiltteredOptions {
  roadmap: DropdownItemModel[];
  unit: DropdownItemModel[];
  lesson: DropdownItemModel[]; // optional
  class: Class[];
}

export interface SkillsCurriculumLessons {
  id: string;
  number: string;
  skills: { id: string }[];
}

export const formStateInitialState: FormState = {
  roadmap: null,
  unit: null,
  lesson: [],
  standards: [],
  class: [],
  unitEnabled: false,
  lessonEnabled: false,
  classEnabled: false,
};

export const availableOptionsInitialState = {
  roadmap: [],
  unit: [],
  lesson: [],
  class: [],
  unitRaw: [],
};

export function buildInitialFormState(
  routerState: { roadmap?: string; roadmapLabel?: string; unit?: string } | null | undefined,
  allUnits:
    | readonly { readonly id: string; readonly name?: string | null; readonly summary?: string | null }[]
    | null
    | undefined,
): FormState {
  if (routerState?.roadmap && routerState?.unit && routerState?.roadmapLabel) {
    const preloadedUnit = allUnits?.find(item => item.id === routerState.unit);
    if (!preloadedUnit) return formStateInitialState;

    return {
      ...formStateInitialState,
      roadmap: { value: routerState.roadmap, label: routerState.roadmapLabel },
      unit: {
        value: routerState.unit,
        label: `${preloadedUnit?.name} - ${preloadedUnit?.summary}`,
      },
      unitEnabled: true,
      classEnabled: true,
    };
  }
  return formStateInitialState;
}

export enum SearchItemsType {
  ROADMAP,
  UNIT,
}

const SingleUnit = () => {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const userRedux = useAppSelector(selectUser);
  const miamiBrand =
    userRedux?.network?.customBranding &&
    mapCustomBranding[userRedux?.network?.customBranding] === CustomBranding.MIAMI;
  const { state: routerDomState } = useLocation();
  const allUnits = useLazyLoadQuery<DashboardAllUnitsQuery>(getAllUnitsGraphQL, { isActive: 'ACTIVE' }).allTargets;

  const [form, setForm] = useState<FormState>(() => buildInitialFormState(routerDomState, allUnits));

  const sideNavActive = useSideNavActive();
  const globalClassIds = useAppSelector(selectSelectedClassIds);
  const [skillSearch, debouncedSkillSearch, setSkillSearch] = useDebounce('', 400);
  const [skillSuggestions, setSkillSuggestions] = useState<Skill[]>([]);
  const [selectedSkill, setSelectedSkill] = useState<Skill | null>(null);
  const [skillDialogVisible, setSkillDialogVisible] = useState(false);
  const isSearchingSkills = sideNavActive && Boolean(debouncedSkillSearch.trim());

  const openSkillDialog = useCallback((skill: Skill) => {
    setSelectedSkill(skill);
    setSkillDialogVisible(true);
  }, []);

  useChildPageTitle(form.unit?.label);
  const [availablesOptions, setAvailablesOptions] = useState<AvailablesOptions>(availableOptionsInitialState);
  const [optionsList, setOptionsList] = useState<AvailablesOptions>(() => ({
    ...availableOptionsInitialState,
    unitRaw: allUnits as Unit[],
  }));
  const [skillsCurriculumLessons, setSkillsCurriculumLessons] = useState<SkillsCurriculumLessons[]>([]);
  const [standardsInUnit, setStandardsInUnit] = useState<DropdownItemModel[]>([]);

  const [firstRender, setFirstRender] = useState<boolean>(true);
  const [classWithoutStudent, setClassWithoutStudent] = useState<boolean>(false);

  const { allClassesData } = useGetClassesByRole(true, true);
  const dispatch = useAppDispatch();
  const rosteredStudents = (form.class?.[0] as Class & { students?: { id?: string }[] })?.students ?? [];
  const hasRosteredStudents = rosteredStudents.length > 0;
  const shouldShowNoRosteredStudentsMessage = classWithoutStudent && !hasRosteredStudents;
  const shouldShowNoSkillRecordsMessage = classWithoutStudent && hasRosteredStudents;
  // Clear tracking context when leaving the Units page
  useEffect(() => {
    return () => {
      dispatch(setTargetGroup(undefined));
      dispatch(setUnit(undefined));
    };
  }, [dispatch]);

  const sitesData = useLazyLoadQuery<SitesTargetGroupsQuery>(
    sitesTargetGroupsQuery,
    { sitesFilter: { userId: `UserNode:${userRedux?.id}` } },
    { fetchPolicy: 'network-only' },
  );

  const sitesDataTemplates =
    sitesData?.sites?.nodes
      ?.map(site => site?.targetGroups?.map(target => ({ value: target?.id ?? '', label: target?.name ?? '' })) ?? [])
      ?.flat()
      ?.sort(sortByKey('label')) ?? [];

  const curriculumLessonsByFilter = useLazyLoadQuery<SingleUnitGetCurriculumLessonsQuery>(getCurriculumLessons, {
    filter: {
      targetGroupId: form.roadmap?.value as string,
      targetIds: [form.unit?.value as string],
      isActive: 'ACTIVE',
    },
    skip: miamiBrand && form.unitEnabled && form.classEnabled ? false : true,
  });

  useEffect(() => {
    if (curriculumLessonsByFilter) {
      const curriculumLessonTemplate =
        curriculumLessonsByFilter?.curriculumLessons
          ?.map(el => ({
            value: el.id,
            label: el.number + ' - ' + el.name,
          }))
          .sort(sortByLabelWithDot) ?? [];

      setSkillsCurriculumLessons(
        (curriculumLessonsByFilter?.curriculumLessons ?? []).map(el => ({
          id: el.id as string,
          number: el.number as string,
          skills: (el.skills ?? []).filter((skill): skill is { id: string } => !!skill && !!skill.id),
        })),
      );

      setOptionsList({
        ...optionsList,
        lesson: curriculumLessonTemplate,
      });
    }
  }, [curriculumLessonsByFilter]);

  useEffect(() => {
    const classesTemplates = allClassesData.map(element => {
      return {
        ...element,
        grades: element?.gradeLevels?.map(grade => grade?.name)?.join(', '),
      };
    });
    setOptionsList({
      ...optionsList,
      class: classesTemplates.map(cl => ({
        ...cl,
        sectionName: cl.section ? `${cl.name} - ${cl.section}` : cl.name,
      })) as Class[],
    });
  }, [allClassesData]);

  useEffect(() => {
    if (!sideNavActive || isUserAdmin(userRedux) || !optionsList.class.length) return;

    const selected = optionsList.class.filter(cls => globalClassIds.includes(cls.id));
    const next = selected.length ? selected : optionsList.class.slice(0, 1);

    if (form.class?.[0]?.id === next[0]?.id) return;
    setForm(prev => ({ ...prev, class: next, classEnabled: true }));
  }, [sideNavActive, globalClassIds, optionsList.class, userRedux]);

  useEffect(() => {
    setOptionsList({
      ...optionsList,
      roadmap: removeTargetDuplicates(sitesDataTemplates),
      unitRaw: allUnits as Unit[],
    });
  }, []);

  useEffect(() => {
    if (form.roadmap) {
      if (firstRender) {
        const unitSelected = optionsList.unitRaw.find((el: Unit) => el.id === `TargetNode:${unitId}`);
        if (unitSelected) {
          const unitItem = {
            value: unitSelected.id,
            label: unitSelected.name + ' - ' + unitSelected.summary,
          };
          setForm({ ...form, unit: unitItem });
        }
        setFirstRender(false);
      } else {
        setForm({ ...form, unit: null });
      }

      const unitsByTargetGroup = optionsList.unitRaw
        .filter((el: Unit) => el.targetGroup?.id === form.roadmap?.value)
        ?.map((element: Unit) => ({
          value: element.id,
          label: element.name + ' - ' + element.summary,
        }))
        .sort(sortByKey('label'));

      setAvailablesOptions({ ...availablesOptions, unit: unitsByTargetGroup });
    }
  }, [form.roadmap]);

  useEffect(() => {
    if (form.unit && miamiBrand) {
      setForm({ ...form, lesson: [] });
    }
  }, [form.unit]);

  const breadcrumbItems = [
    { label: 'Plan', command: () => navigate('/planning') },
    { label: 'Units', command: () => navigate('/units') },
  ];

  return (
    <div className="nc_page-inner nc_page-side-margin flex-grow-1 overflow-x-hidden">
      <div className="grid">
        <div className="col-10 col-offset-1 lg:col-12 lg:col-offset-0 flex flex-column nc_content-frame">
          {!sideNavActive && (
            <>
              <Toolbar role="section" center={<h1>Units</h1>} className="col-12 nc_page-header" />
              <BreadCrumb
                model={breadcrumbItems}
                home={{ icon: 'nc_icon-nc_home icon-sm', command: () => navigate('/') }}
                className="col-12 nc_breadcrumb"
              />
            </>
          )}
          <div className="col-10 col-offset-1 lg:col-12 lg:col-offset-0 nc_block flex flex-column justify-content-start grid">
            {sideNavActive ? (
              <UnitsReportSettings
                form={form}
                setForm={setForm}
                optionsList={optionsList}
                availablesOptions={availablesOptions}
                searchQuery={skillSearch}
                onSearchChange={setSkillSearch}
                skillSuggestions={skillSuggestions}
                onSkillSelect={openSkillDialog}
              />
            ) : null}
            {isSearchingSkills && (
              <Suspense fallback={null}>
                <SkillSearchResults searchQuery={debouncedSkillSearch} onResults={setSkillSuggestions} />
              </Suspense>
            )}
            {sideNavActive && form.roadmap && form.unit && (
              <UnitsOptionalFilters
                form={form}
                setForm={setForm}
                classes={optionsList.class}
                standards={standardsInUnit}
                showClasses={isUserAdmin(userRedux)}
              />
            )}
            {!sideNavActive && (
              <SingleUnitFilter
                miamiBrand={miamiBrand as boolean}
                form={form}
                setForm={setForm}
                availablesOptions={availablesOptions}
                optionsList={optionsList}
              />
            )}
            {
              <div className="nc_content-frame">
                {shouldShowNoRosteredStudentsMessage && (
                  <div className="w-full text-center pb-4">
                    <h3 className="m-0">
                      There is no student data to show because this class has no students rostered.
                    </h3>
                  </div>
                )}
                {shouldShowNoSkillRecordsMessage && (
                  <div className="w-full text-center pb-4">
                    <h3 className="m-0">
                      Student data will be available once the diagnostic has been completed.{' '}
                      <a
                        className=" tw-font-semibold tw-text-lg p-0"
                        onClick={() =>
                          navigate('/diagnostics', {
                            state: {
                              selectedSiteId: form.class?.[0]?.site?.id,
                              selectedClassId: form.class?.[0]?.id,
                              selectedRoadmapId: form.roadmap?.value,
                            },
                          })
                        }
                      >
                        Click here to view this class&apos;s diagnostic progress.
                      </a>
                    </h3>
                  </div>
                )}
                {form.unit && (
                  <SingleUnitContainer
                    selectedUnit={form.unit}
                    selectedClass={form.class}
                    selectedLesson={form.lesson}
                    selectedStandards={form.standards}
                    onStandardsLoaded={setStandardsInUnit}
                    setClassWithoutStudent={setClassWithoutStudent}
                    skillsCurriculumLessons={skillsCurriculumLessons}
                    miamiBrand={miamiBrand as boolean}
                  />
                )}
              </div>
            }
          </div>
        </div>
      </div>
      {skillDialogVisible && selectedSkill && (
        <SingleUnitSkillDialog visible={skillDialogVisible} setVisible={setSkillDialogVisible} skill={selectedSkill} />
      )}
    </div>
  );
};

const SingleUnitWrapper = () => {
  return (
    <Suspense fallback={<ThreeDotLoader />}>
      <SingleUnit />
    </Suspense>
  );
};

export default SingleUnitWrapper;
